import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { renderRoute, routeDemographicSurvey, routePaymentSetup } from "@navigation/typedRoutes";
import { usePaymentClient } from "@contexts/PaymentClient.context";
import { useLoadingController } from "@contexts/Loading.context";
import ChooseSubscriptionComponent from "@view/payment/ChooseSubscription";

import { makeJwtSubscriptionStatus } from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import { ok } from "@ps-bridge/ok";
import { mkNavigate, mkRouteBuilder, mkTaskRunner } from "@ps-bridge/capabilities";
import type { TaskTs } from "@ps-bridge/interop";
import { ioEffect } from "@utils/effects/branded";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import * as Option from "@utils/Option";
import { getParamOption } from "@utils/searchParams";
import { useIoState } from "@utils/useIoState";
import { useIoOptionState } from "@utils/useIoOptionState";
import type { ChooseSubscriptionKernel } from "@ps-bridge/contracts/chooseSubscription";
import { mkChooseSubscriptionController } from "@ps-bridge/controllers/chooseSubscription";
import { buildChooseSubscriptionViewProps } from "@ps-bridge/chooseSubscription.program";
import type { Ok } from "@ps-bridge/ok";
import type { DemographicInformation, PromoCode, SubscriptionPlan } from "@ps-bridge/models/core/dataContracts";

const ChooseSubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentClient = usePaymentClient();
  const loading = useLoadingController();
  const subscriptionStatusValue = useMemo(
    () => getParamOption(searchParams, "status"),
    [searchParams],
  );
  const subscriptionStatus = useMemo(
    () => makeJwtSubscriptionStatus(Option.fold(subscriptionStatusValue, () => "FreeTrialMode", (value) => value)),
    [subscriptionStatusValue],
  );
  const runTaskWithError = useRunTaskWithError();
  const runTaskWithErrorIo = useCallback(
    (task: TaskTs<Ok>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );
  const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);
  const selectionLoading = useIoState(false);
  const promoLoading = useIoState(false);
  const promoCodeError = useIoOptionState<string>(Option.none());
  const { read: selectionLoadingRead, set: selectionLoadingSet } = selectionLoading;
  const { read: promoLoadingRead, set: promoLoadingSet } = promoLoading;
  const { read: promoCodeErrorRead, setSome: promoCodeErrorSetSome, setMaybe: promoCodeErrorSetMaybe } = promoCodeError;
  const selectionLoadingHandle = useMemo(
    () => ({ read: selectionLoadingRead, set: selectionLoadingSet }),
    [selectionLoadingRead, selectionLoadingSet],
  );
  const promoLoadingHandle = useMemo(
    () => ({ read: promoLoadingRead, set: promoLoadingSet }),
    [promoLoadingRead, promoLoadingSet],
  );
  const promoCodeErrorHandle = useMemo(
    () => ({
      read: promoCodeErrorRead,
      setSome: promoCodeErrorSetSome,
      setMaybe: promoCodeErrorSetMaybe,
    }),
    [promoCodeErrorRead, promoCodeErrorSetSome, promoCodeErrorSetMaybe],
  );
  const paymentClientKernel = useMemo(
    () => ({
      getPublishableKey: paymentClient.getPublishableKey,
      setupIntent: (subscriptionPlan: SubscriptionPlan) => paymentClient.setupIntent(subscriptionPlan),
      cancelSubscription: paymentClient.cancelSubscription,
      validatePromoCode: (promoCode: PromoCode) => paymentClient.validatePromoCode(promoCode),
      submitSquareCardNonce: (nonce: string) => paymentClient.submitSquareCardNonce(nonce),
      submitDemographicInformation:
        (promoCode: PromoCode) =>
        (demographicInfo: DemographicInformation) =>
          paymentClient.submitDemographicInformation(promoCode, demographicInfo),
    }),
    [paymentClient],
  );
  const paymentSetupRoute = useMemo(() => mkRouteBuilder(routePaymentSetup), []);
  const demographicSurveyRoute = useMemo(() => mkRouteBuilder(routeDemographicSurvey), []);
  const navigateCapability = useMemo(
    () =>
      mkNavigate((route) =>
        ioEffect(() => {
          navigate(renderRoute(route));
          return ok;
        }),
      ),
    [navigate],
  );
  const controller = useMemo(
    () =>
      mkChooseSubscriptionController({
        loading,
        paymentClient: paymentClientKernel,
        navigate: navigateCapability,
        taskRunner,
        selectionLoading: selectionLoadingHandle,
        promoLoading: promoLoadingHandle,
        promoCodeError: promoCodeErrorHandle,
        paymentSetupRoute,
        demographicSurveyRoute,
      }),
    [
      demographicSurveyRoute,
      loading,
      navigateCapability,
      paymentClientKernel,
      paymentSetupRoute,
      promoCodeErrorHandle,
      promoLoadingHandle,
      selectionLoadingHandle,
      taskRunner,
    ],
  );
  const kernel: ChooseSubscriptionKernel = useMemo(
    () => ({
      controller,
      loading,
      paymentClient: paymentClientKernel,
      taskRunner,
      selectionLoading: selectionLoadingHandle,
      promoLoading: promoLoadingHandle,
      promoCodeError: promoCodeErrorHandle,
      navigate: navigateCapability,
    }),
    [
      controller,
      loading,
      navigateCapability,
      paymentClientKernel,
      promoCodeErrorHandle,
      promoLoadingHandle,
      selectionLoadingHandle,
      taskRunner,
    ],
  );
  const viewProps = useMemo(
    () =>
      buildChooseSubscriptionViewProps({
        kernel,
        subscriptionStatus,
      }),
    [kernel, subscriptionStatus],
  );

  return (
    <ChooseSubscriptionComponent
      {...viewProps}
    />
  );
};

export default ChooseSubscriptionPage;
